import {
    ButtonGradientPrimary,
    ButtonSecondary,
    Icon,
    ModalDialog,
    Pills,
} from '@amelio/ui-library';
import { useEffect } from 'react';
import { FormProvider } from 'react-hook-form';

import { GROWTH_CAPTION } from './growthStyles';

import type { CareerAspiration } from '@/types/career-growth';

import { BlankAspirationConfirmDialog } from '@/components/CareerAspiration/BlankAspirationConfirmDialog';
import { CareerAspirationForm } from '@/components/CareerAspiration/CareerAspirationForm';
import { GROWTH_FORM_GROUND } from '@/components/CareerAspiration/careerAspirationStyles';
import { toFormValues } from '@/components/CareerAspiration/careerAspirationValidation';
import { useCareerAspirationForm } from '@/components/CareerAspiration/useCareerAspirationForm';
import { useAmelioTranslations } from '@/hooks';

interface CareerAspirationEditorModalProps {
    isOpen: boolean;
    onClose: () => void;
    employeeId: string;
    employeeFirstName: string;
    /** The aspiration being edited, or `null` to record a new one. */
    aspiration: CareerAspiration | null;
    /**
     * The career meeting a new aspiration is recorded against (a pending entry of the history);
     * `null` records it from the tab, without a meeting. Ignored when editing: an aspiration keeps
     * the meeting it was recorded in.
     */
    thematicMeetingId: string | null;
    dataTestId?: string;
}

/**
 * The badge and the sentence the results section already wear, moved to the top of the dialog: who
 * reads these answers is worth knowing before writing them, not after.
 *
 * The sentence reuses `aspiration.form.visibility`, the exact wording already shown on the
 * career-aspiration banner (`CareerAspirationBanner`): same scope (`EmployeeAccessScope`,
 * `PerformanceReachScope`), same promise, one wording kept in one place.
 */
const ConfidentialNotice = () => {
    const { t } = useAmelioTranslations();

    return (
        <div className={'flex flex-wrap items-center gap-2 py-3'}>
            <Pills
                variant={'default'}
                size={'audience'}
            >
                <Icon
                    name={'security-lock'}
                    size={12}
                    color={'inherit'}
                    ariaHidden
                />
                {t('performanceProfile.growth.aspiration.confidential')}
            </Pills>
            <p className={GROWTH_CAPTION}>
                {t('performanceProfile.growth.aspiration.form.visibility')}
            </p>
        </div>
    );
};

/**
 * "Modifier les aspirations de carrière": the meeting's questions in a dialog, saved in one
 * request -- the whole picture at once, the way the API replaces it. The form is reset from the
 * aspiration each time the dialog opens, so a cancelled edit never leaks into the next one.
 *
 * It asks for the widest size the library offers, the one the other dialogs that hold a whole form
 * already take: five questions, their follow-ups and their helper copy read as a page, and the
 * narrower size broke the paths into three rows of chips before the first answer was given.
 */
export const CareerAspirationEditorModal = ({
    isOpen,
    onClose,
    employeeId,
    employeeFirstName,
    aspiration,
    thematicMeetingId,
    dataTestId = 'career-aspiration-editor',
}: CareerAspirationEditorModalProps) => {
    const { t } = useAmelioTranslations();
    const { form, save, isSaving, blankSaveConfirm } = useCareerAspirationForm(
        employeeId,
        aspiration,
        thematicMeetingId,
    );
    const { reset, handleSubmit } = form;

    useEffect(() => {
        if (isOpen) {
            reset(toFormValues(aspiration));
        }
    }, [isOpen, aspiration, reset]);

    const saveAndClose = handleSubmit(async (values) => {
        if (await save(values)) {
            onClose();
        }
    });

    return (
        <ModalDialog
            isOpen={isOpen}
            onClose={onClose}
            closeAriaLabel={t('common.close')}
            title={t('performanceProfile.growth.aspiration.form.title')}
            subtitle={t('performanceProfile.growth.aspiration.form.subtitle', {
                name: employeeFirstName,
            })}
            size={'lg'}
            chrome={'banded'}
            titleIcon={
                <Icon
                    name={'location-route-square'}
                    size={'lg'}
                    ariaHidden
                />
            }
            headerExtra={<ConfidentialNotice />}
            onSubmit={saveAndClose}
            dataTestId={dataTestId}
            radius={'4xl'}
            shadow={'2xl'}
            bordered
            backdropBlur
            footer={
                <div className={'flex w-full flex-wrap items-center justify-between gap-3'}>
                    <span className={`${GROWTH_CAPTION} inline-flex items-center gap-1.5`}>
                        <Icon
                            name={'security-lock'}
                            size={'sm'}
                            ariaHidden
                        />
                        {t('performanceProfile.growth.aspiration.form.footerHint')}
                    </span>

                    <div className={'ml-auto flex items-center gap-2'}>
                        <ButtonSecondary
                            size={'sm'}
                            onClick={onClose}
                            disabled={isSaving}
                            dataTestId={`${dataTestId}-cancel`}
                        >
                            {t('common.cancel')}
                        </ButtonSecondary>
                        <ButtonGradientPrimary
                            type={'submit'}
                            size={'sm'}
                            loading={isSaving}
                            disabled={isSaving}
                            // The library swaps the label for a spinner while saving; the name holds.
                            ariaLabel={t('performanceProfile.growth.aspiration.form.save')}
                            dataTestId={`${dataTestId}-save`}
                        >
                            {t('performanceProfile.growth.aspiration.form.save')}
                        </ButtonGradientPrimary>
                    </div>
                </div>
            }
        >
            <FormProvider {...form}>
                <div className={GROWTH_FORM_GROUND}>
                    <CareerAspirationForm
                        employeeFirstName={employeeFirstName}
                        dataTestId={`${dataTestId}-form`}
                    />
                </div>
            </FormProvider>

            <BlankAspirationConfirmDialog {...blankSaveConfirm} />
        </ModalDialog>
    );
};
